import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { DEFAULT_LIST_PAGE_SIZE } from '@/components/shared/ListPagination';
import { api } from '@/services/api';
import { ApiError } from '@/services/httpClient';
import type { Application } from '@/types';
import {
  isApplicationBulkProcessable,
  isApplicationToolbarProcessable,
  matchesApplicationStatusFilter,
  type ApplicationStatusFilterQueue,
} from '@/lib/pendingProcessing';

/** Row selection with Shift+click range select (anchor → current row). */
export function useShiftClickSelection<T extends { id: string }>(
  /** Current table page rows — header checkbox selects only these. */
  visibleRows: T[],
  initialSelectedIds?: string[],
  controlled?: {
    selectedIds: Set<string>;
    setSelectedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  },
) {
  const [internalSelectedIds, setInternalSelectedIds] = useState<Set<string>>(
    () => new Set(initialSelectedIds ?? []),
  );
  const selectedIds = controlled?.selectedIds ?? internalSelectedIds;
  const setSelectedIds = controlled?.setSelectedIds ?? setInternalSelectedIds;
  const anchorIdRef = useRef<string | null>(null);
  const visibleRowIdsRef = useRef<string[]>([]);
  visibleRowIdsRef.current = visibleRows.map(row => row.id);
  const visibleRowIdsKey = visibleRowIdsRef.current.join('\0');

  useEffect(() => {
    anchorIdRef.current = null;
  }, [visibleRowIdsKey]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    anchorIdRef.current = null;
  }, []);

  /** Add every row on the current table page to the selection (accumulates across pages). */
  const selectAllOnPage = useCallback(() => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      for (const id of visibleRowIdsRef.current) next.add(id);
      return next;
    });
    anchorIdRef.current = null;
  }, [setSelectedIds]);

  /** Remove current-page rows from the selection; keeps selections on other pages. */
  const deselectAllOnPage = useCallback(() => {
    const pageIds = new Set(visibleRowIdsRef.current);
    setSelectedIds(prev => {
      const next = new Set([...prev].filter(id => !pageIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
    anchorIdRef.current = null;
  }, [setSelectedIds]);

  /** @deprecated use selectAllOnPage */
  const selectAllMatching = selectAllOnPage;
  const selectAllVisible = selectAllOnPage;

  const handleRowSelect = useCallback((index: number, id: string, shiftKey: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      const anchorIndex = anchorIdRef.current === null
        ? -1
        : visibleRows.findIndex(row => row.id === anchorIdRef.current);

      if (shiftKey && anchorIndex >= 0) {
        const start = Math.min(anchorIndex, index);
        const end = Math.max(anchorIndex, index);
        const shouldSelect = !prev.has(id);
        for (let i = start; i <= end; i += 1) {
          const rowId = visibleRows[i]?.id;
          if (!rowId) continue;
          if (shouldSelect) next.add(rowId);
          else next.delete(rowId);
        }
      } else {
        if (next.has(id)) next.delete(id);
        else next.add(id);
        anchorIdRef.current = id;
      }

      return next;
    });
  }, [visibleRows]);

  return {
    selectedIds,
    setSelectedIds,
    clearSelection,
    selectAllOnPage,
    deselectAllOnPage,
    selectAllMatching,
    selectAllVisible,
    handleRowSelect,
  };
}

/** Max rows per bulk Process / Review auto-select — matches table page size. */
export const BULK_PROCESS_PAGE_LIMIT = DEFAULT_LIST_PAGE_SIZE;

/** Banner → Applications auto-select (same rules as status filter). */
export function getAutoSelectableApplications(
  rows: Application[],
  hasProcessingCredits: boolean,
  statusFilter: string,
  queue?: ApplicationStatusFilterQueue,
): Application[] {
  return rows.filter((app) => {
    if (app.archived === true) return false;
    if (statusFilter === 'READY_TO_PROCESS' || statusFilter === 'PENDING_UPGRADE') {
      return matchesApplicationStatusFilter(app, statusFilter, hasProcessingCredits, queue);
    }
    return isApplicationToolbarProcessable(app, hasProcessingCredits);
  });
}

/** @deprecated Prefer getAutoSelectableApplications — kept for call sites that only need one page. */
export function getProcessableApplicationsOnPage(
  pageRows: Application[],
  hasProcessingCredits: boolean,
  mode: 'ready' | 'toolbar',
  queue?: ApplicationStatusFilterQueue,
): Application[] {
  const statusFilter = mode === 'ready' ? 'READY_TO_PROCESS' : 'all';
  return getAutoSelectableApplications(pageRows, hasProcessingCredits, statusFilter, queue);
}

/** Header checkbox — checked when any row on this page is selected; toggles all on/off for the page. */
export function SelectAllHeaderCheckbox({
  pageRows,
  selectedIds,
  selectAllOnPage,
  deselectAllOnPage,
  clearSelection,
  ariaLabel,
}: {
  pageRows: { id: string }[];
  selectedIds: Set<string>;
  selectAllOnPage: () => void;
  deselectAllOnPage?: () => void;
  clearSelection: () => void;
  ariaLabel: string;
}) {
  const pageRowIds = pageRows.map(row => row.id);
  const pageHasSelection = pageRowIds.some(id => selectedIds.has(id));

  return (
    <input
      type="checkbox"
      className="h-3.5 w-3.5 rounded border-input accent-primary cursor-pointer block mx-auto"
      checked={pageHasSelection}
      onClick={(e) => e.stopPropagation()}
      onChange={() => {
        if (pageHasSelection) {
          if (deselectAllOnPage) deselectAllOnPage();
          else clearSelection();
        } else {
          selectAllOnPage();
        }
      }}
      aria-label={ariaLabel}
    />
  );
}

/** @deprecated Prefer `<SelectAllHeaderCheckbox />` */
export function renderSelectAllHeaderCheckbox(options: {
  pageRows: { id: string }[];
  selectedIds: Set<string>;
  selectAllOnPage: () => void;
  deselectAllOnPage?: () => void;
  clearSelection: () => void;
  ariaLabel: string;
  /** @deprecated use selectAllOnPage */
  selectAllMatching?: () => void;
}): ReactNode {
  const selectPage = options.selectAllOnPage ?? options.selectAllMatching!;
  return (
    <SelectAllHeaderCheckbox
      pageRows={options.pageRows}
      selectedIds={options.selectedIds}
      selectAllOnPage={selectPage}
      deselectAllOnPage={options.deselectAllOnPage}
      clearSelection={options.clearSelection}
      ariaLabel={options.ariaLabel}
    />
  );
}

export function getSelectedProcessableApps(
  selectedIds: Set<string>,
  applications: Application[],
  hasProcessingCredits: boolean,
  options?: { limit?: number },
): Application[] {
  const apps = [...selectedIds]
    .map(id => applications.find(a => a.id === id))
    .filter(
      (app): app is Application =>
        !!app && isApplicationBulkProcessable(app, hasProcessingCredits),
    );
  const limit = options?.limit;
  if (limit != null && limit > 0) return apps.slice(0, limit);
  return apps;
}

export function mergeApplicationsById(
  prev: Application[],
  updates: Application[],
): Application[] {
  if (updates.length === 0) return prev;
  const byId = new Map(updates.map(u => [u.id, u]));
  return prev.map(a => byId.get(a.id) ?? a);
}

/** Server processes sequentially — aligned with table page size to reduce stuck pipelines. */
export const BULK_PROCESS_BATCH_SIZE = BULK_PROCESS_PAGE_LIMIT;

/** bulk-abort-stuck runs sequentially on the server — stay under the 60s function limit. */
export const BULK_ABORT_BATCH_SIZE = 15;

function toBulkItems(apps: Application[]): Array<{ applicationId: string; positionId: string }> {
  return apps.map(app => ({
    applicationId: app.id,
    positionId: app.positionId,
  }));
}

export type BulkProcessNowAggregate = {
  processed: number;
  skippedBilling: number;
  errors: number;
  total: number;
};

function isBulkProcessRouteMissing(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

/** Bulk route missing, timed out, or gateway error (often surfaced as CORS in the browser). */
function shouldFallbackBulkAbort(err: unknown): boolean {
  if (err instanceof ApiError) {
    return err.status === 404 || err.status === 502 || err.status === 503 || err.status === 504;
  }
  return err instanceof TypeError;
}

function mergeBulkAbortResults(
  a: BulkAbortStuckAggregate,
  b: BulkAbortStuckAggregate,
): BulkAbortStuckAggregate {
  return {
    aborted: a.aborted + b.aborted,
    skipped: a.skipped + b.skipped,
    errors: a.errors + b.errors,
    total: a.total + b.total,
  };
}

export type BulkAbortStuckAggregate = {
  aborted: number;
  skipped: number;
  errors: number;
  total: number;
};

/** Legacy API — one abort-stuck per row when bulk-abort-stuck is not deployed. */
async function bulkAbortStuckViaAbortStuck(
  companyId: string,
  apps: Application[],
): Promise<BulkAbortStuckAggregate> {
  let aborted = 0;
  let skipped = 0;
  let errors = 0;
  const total = apps.length;

  for (const app of apps) {
    if (app.archived === true) {
      skipped += 1;
      continue;
    }
    if (app.state !== 'PIPELINE_RUNNING') {
      skipped += 1;
      continue;
    }
    const positionId = app.positionId;
    if (!positionId) {
      errors += 1;
      continue;
    }
    try {
      await api.applications.abortStuck(companyId, positionId, app.id);
      aborted += 1;
      await new Promise((r) => setTimeout(r, 80));
    } catch (err) {
      if (isBulkProcessRouteMissing(err)) throw err;
      errors += 1;
    }
  }

  return { aborted, skipped, errors, total };
}

/**
 * Phase 1 of bulk restart — stop stuck pipelines without re-running AI.
 */
export async function runBulkAbortStuck(
  companyId: string,
  apps: Application[],
): Promise<BulkAbortStuckAggregate> {
  if (apps.length === 0) {
    return { aborted: 0, skipped: 0, errors: 0, total: 0 };
  }

  let aggregate: BulkAbortStuckAggregate = {
    aborted: 0,
    skipped: 0,
    errors: 0,
    total: 0,
  };

  for (let offset = 0; offset < apps.length; offset += BULK_ABORT_BATCH_SIZE) {
    const batchApps = apps.slice(offset, offset + BULK_ABORT_BATCH_SIZE);
    const batchItems = toBulkItems(batchApps);
    try {
      const result = await api.applications.bulkAbortStuck(companyId, batchItems);
      aggregate = mergeBulkAbortResults(aggregate, result);
    } catch (err) {
      if (!shouldFallbackBulkAbort(err)) throw err;
      if (import.meta.env.DEV) {
        console.warn(
          '[bulk-process] bulk-abort-stuck batch failed — using per-application abort-stuck',
          err,
        );
      }
      const legacy = await bulkAbortStuckViaAbortStuck(companyId, batchApps);
      aggregate = mergeBulkAbortResults(aggregate, legacy);
    }
  }

  return { ...aggregate, total: apps.length };
}

/** @deprecated Use runBulkAbortStuck */
export const runBulkAbortStuckIfAvailable = runBulkAbortStuck;

/** Fallback when `bulk-process-now` is not deployed yet (older Cloud Functions). */
async function bulkProcessBatchViaProcessNow(
  companyId: string,
  batch: Array<{ applicationId: string; positionId: string }>,
): Promise<BulkProcessNowAggregate> {
  let processed = 0;
  let errors = 0;
  const total = batch.length;

  for (const item of batch) {
    try {
      await api.applications.processNow(companyId, item.positionId, item.applicationId);
      processed += 1;
      // Pace requests so sync polling + process-now do not exhaust browser connections.
      await new Promise((r) => setTimeout(r, 80));
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        return {
          processed,
          skippedBilling: total - processed - errors,
          errors,
          total,
        };
      }
      errors += 1;
    }
  }

  return { processed, skippedBilling: 0, errors, total };
}

/**
 * Queue screening in batches. Stops when the plan cap is hit (`skippedBilling`).
 */
export async function runBulkProcessNowBatched(
  companyId: string,
  apps: Application[],
  options?: {
    batchSize?: number;
    onBatchComplete?: (done: number, total: number) => void;
  },
): Promise<BulkProcessNowAggregate> {
  const items = toBulkItems(apps);
  const batchSize = options?.batchSize ?? BULK_PROCESS_BATCH_SIZE;
  let processed = 0;
  let skippedBilling = 0;
  let errors = 0;
  let useLegacyProcessNow = false;

  for (let offset = 0; offset < items.length; offset += batchSize) {
    const batch = items.slice(offset, offset + batchSize);
    let result: BulkProcessNowAggregate;
    if (useLegacyProcessNow) {
      result = await bulkProcessBatchViaProcessNow(companyId, batch);
    } else {
      try {
        result = await api.applications.bulkProcessNow(companyId, batch);
      } catch (err) {
        if (!isBulkProcessRouteMissing(err)) throw err;
        useLegacyProcessNow = true;
        if (import.meta.env.DEV) {
          console.warn(
            '[bulk-process] POST bulk-process-now not found — using per-application process-now',
          );
        }
        result = await bulkProcessBatchViaProcessNow(companyId, batch);
      }
    }
    processed += result.processed;
    errors += result.errors;
    if (result.skippedBilling > 0) {
      skippedBilling += result.skippedBilling;
      options?.onBatchComplete?.(items.length, items.length);
      break;
    }
    options?.onBatchComplete?.(Math.min(offset + batch.length, items.length), items.length);
  }

  return { processed, skippedBilling, errors, total: items.length };
}

/**
 * @deprecated Prefer runBulkProcessNowBatched via useApplicationProcessQueue.
 */
export async function runBulkProcessNow(
  companyId: string,
  apps: Application[],
): Promise<{ ok: number; fail: number; updated: Application[] }> {
  const result = await runBulkProcessNowBatched(companyId, apps);
  return {
    ok: result.processed,
    fail: result.errors + result.skippedBilling,
    updated: [],
  };
}
